<script lang="ts" setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { formatDate } from '@/utils/date-utils';
import { ProjectType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BTable,
} from 'bootstrap-vue-next';
import { openRouterLink } from '@/utils/router-utils';

const router = useRouter();

// --- Extended Project Inteface ---
export interface ProjectTypeExtended extends ProjectType {
  canEdit: boolean;
}

// --- Component properties ---
const props = defineProps<{
  projects: ProjectTypeExtended[];
  loading: boolean;
  showOwner: boolean;
}>();

const noProjects = computed(() => {
  return props.projects.length === 0 && !props.loading;
});

// --- Project Table Fields ---
const projectTableFields = computed(() => {
  const fields = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      tdClass: 'align-middle',
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      tdClass: 'align-middle',
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      formatter: formatDate,
      tdClass: 'align-middle',
    },
    { key: 'actions', label: '', tdClass: 'align-middle text-end' },
  ];
  // Kit field (optional)
  if (props.showOwner) {
    fields.splice(2, 0, {
      key: 'owner',
      label: 'Project owner',
      sortable: true,
      tdClass: 'align-middle',
    });
  }
  return fields;
});

function handleProjectRowClick(
  item: ProjectTypeExtended,
  index?: number,
  evt?: MouseEvent
) {
  openRouterLink(
    { name: 'ProjectDetail', params: { id: item.id } },
    router,
    evt
  );
}
</script>
<template>
  <div>
    <b-alert v-model="noProjects" variant="secondary"
      >No projects found.</b-alert
    >
    <BCard v-if="props.projects.length > 0" class="mb-3">
      <b-table
        :items="props.projects"
        :busy="props.loading"
        :fields="projectTableFields"
        small
        responsive
        hover
        striped
        class="browser-table"
        @row-clicked="handleProjectRowClick"
      >
        <template #cell(name)="data">
          <strong>{{ data.value }}</strong>
        </template>
        <template #cell(actions)="data">
          <BButtonGroup>
            <BButton
              size="sm"
              variant="outline-secondary"
              @click="handleProjectRowClick(data.item, data.item.id, $event)"
              @click.stop
            >
              <font-awesome-icon icon="eye" />
            </BButton>
          </BButtonGroup>
        </template>
      </b-table>
    </BCard>
  </div>
</template>
